import { computed, inject, untracked } from '@angular/core';
import { patchState, signalStore, withComputed, withFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';

import { AssetName } from '@core/api/assets/assets.models';
import { AssetsService } from '@core/api/assets/assets.service';

import { withApiResource } from '@shared/stores/features/with-api-resource.feature';
import { withCancellation } from '@shared/stores/features/with-cancellation.feature';

interface VehicleNamesState {
  siteId: string | undefined;
  vehicleNames: AssetName[] | undefined;
}

const initialState: VehicleNamesState = {
  siteId: undefined,
  vehicleNames: undefined,
};

export const VehicleNamesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCancellation('clear'),
  withFeature((store) => {
    const assetsService = inject(AssetsService);
    return withApiResource('vehicleNames', (siteId: string) => assetsService.getVehicleNames$(siteId), store.clear$);
  }),
  withComputed(({ vehicleNames, vehicleNamesIsLoading, vehicleNamesError }) => ({
    vehicleNamesById: computed(() => Object.fromEntries((vehicleNames() ?? []).map((vehicle) => [vehicle.id, vehicle.name]))),
    isLoading: computed(() => vehicleNamesIsLoading()),
    error: computed(() => (vehicleNamesError() ? 'Failed to load vehicle names' : undefined)),
  })),
  withMethods((store) => {
    const applyLoadSuccess = rxMethod<{ assets: AssetName[] }>(pipe(tap(({ assets }) => patchState(store, { vehicleNames: assets }))));
    applyLoadSuccess(store.vehicleNamesLoadSuccess$);

    return {
      ensureVehicleNamesLoaded(siteId: string): void {
        const siteIsLoadedOrLoading = untracked(
          () => store.siteId() === siteId && (store.vehicleNames() !== undefined || store.isLoading())
        );
        if (siteIsLoadedOrLoading) {
          return;
        }
        patchState(store, { siteId, vehicleNames: undefined });
        store.vehicleNamesLoad(siteId);
      },

      clearVehicleNames(): void {
        store.clear();
        patchState(store, initialState);
      },

      vehicleNamesForSite(siteId: string): AssetName[] | undefined {
        return store.siteId() === siteId ? store.vehicleNames() : undefined;
      },

      vehicleNameForSite(siteId: string, vehicleId: string): string | undefined {
        return store.siteId() === siteId ? store.vehicleNamesById()[vehicleId] : undefined;
      },

      vehicleNamesHaveLoadedForSite(siteId: string): boolean {
        return store.siteId() === siteId && store.vehicleNames() !== undefined;
      },

      vehicleExistsForSite(siteId: string, vehicleId: string): boolean {
        return store.siteId() === siteId && store.vehicleNames()?.some((vehicle) => vehicle.id === vehicleId) === true;
      },

      vehicleNamesAreLoadingForSite(siteId: string): boolean {
        return store.siteId() !== siteId || store.isLoading();
      },

      vehicleNamesErrorForSite(siteId: string): string | undefined {
        return store.siteId() === siteId ? store.error() : undefined;
      },
    };
  })
);

export type VehicleNamesStoreInstance = InstanceType<typeof VehicleNamesStore>;
